import { useQuery } from '@tanstack/react-query'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { Button } from 'components/ui/Button'
import { formatDistance } from 'date-fns'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { $fetch, useFetch } from 'utils/fetch'
import ThreadMessage from './Message'

function getThreadPreview(thread) {
  if (thread.messages.length === 0)
    return 'No messages'
  const lastMessage = thread.messages[thread.messages.length - 1]
  return lastMessage.content
}

export default function Logs() {
  const id = useParams().id
  const [selectedThreadId, setSelectedThreadId] = useState()
  const navigate = useNavigate()

  const { data: threadsData, isLoading: loadingThreads } = useFetch(`/v1/widget/${id}/threads`)
  const { data: widgetData, isLoading: isLoadingWidget } = useQuery({
    queryKey: ['widget'],
    queryFn: async () => {
      const response = await $fetch('/v1/widgets', { method: 'GET' })
      return response?.data?.[0]
    },
  })
  const primaryColor = widgetData?.config?.primaryColor ?? '#000000'

  useEffect(() => {
    if (!selectedThreadId && threadsData) {
      setSelectedThreadId(threadsData?.[0]?.id)
    }
  }, [threadsData, selectedThreadId])

  const selectedThread = threadsData?.find(thread => thread.id === selectedThreadId)

  const loading = loadingThreads || isLoadingWidget
  if (loading) {
    return (
      <div className="flex w-full flex-1">
        <div>
          <TopBarContent>
            <TopBar.Title>Web Widget</TopBar.Title>
            <TopBar.Actions>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/dashboard/web-widget`)}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Widget
              </Button>
            </TopBar.Actions>
          </TopBarContent>
          <div className="flex h-full w-full items-center justify-center">
            <GradientLoadingAnimation message="Loading Widget Logs" variant="roseGarden" rounded="full" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-1">
      <TopBarContent>
        <TopBar.Title>Web Widget Chat logs</TopBar.Title>
        <TopBar.Actions>
          <Button
            size="sm"
            variant="secondary"
            onPress={() => navigate(`/dashboard/web-widget`)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Widget
          </Button>
        </TopBar.Actions>
      </TopBarContent>

      <div className="flex h-full flex-1 bg-white">
        <div className="w-full overflow-y-auto border-r border-gray-200">
          <div className="p-4">
            {threadsData?.map(thread => (
              <div
                key={thread.id}
                onClick={() => setSelectedThreadId(thread.id)}
                className={`
                  cursor-pointer border-b border-gray-100 p-4 transition-colors
                  hover:bg-gray-50
                  ${
              selectedThreadId === thread.id
                ? `border-l-4 border-l-blue-500 bg-blue-50`
                : ''
              }
                `}
              >
                <div className="mb-2 flex items-start justify-between">
                  <h3 className={`
                    max-w-md truncate text-sm font-medium text-gray-900
                  `}
                  >
                    {getThreadPreview(thread)}
                  </h3>
                  <span className="ml-2 shrink-0 text-xs text-gray-500">{formatDistance(new Date(thread.created_at), new Date(), { addSuffix: true })}</span>
                </div>
                <p className="text-xs text-gray-500">
                  {thread.messages.length}
                  {' '}
                  message
                  {thread.messages.length !== 1 ? 's' : ''}
                </p>
              </div>
            ))}

            {(!threadsData || threadsData.length === 0) && (
              <div className="p-8 text-center text-gray-500">
                <p>No chat threads found</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col">
          {selectedThread
            ? (
                <>
                  <div className="border-b border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-medium text-gray-900">Thread Details</h2>
                      <span className="text-sm text-gray-500">
                        {`Started ${formatDistance(new Date(selectedThread.created_at), new Date(), { addSuffix: true })}`}
                      </span>
                    </div>
                  </div>

                  <div className={`
                    flex flex-1 flex-col gap-y-4 overflow-y-auto p-4
                  `}
                  >
                    {selectedThread.messages.map(message => (
                      <ThreadMessage key={message.id} message={message} primaryColor={primaryColor} isUser={message.sender_type === 'USER'} />
                    ))}

                    {selectedThread.messages.length === 0 && (
                      <div className="py-8 text-center text-gray-500">
                        <p>No messages in this thread</p>
                      </div>
                    )}
                  </div>
                </>
              )
            : (
                <div className={`
                  flex flex-1 items-center justify-center text-gray-500
                `}
                >
                  <div className="text-center">
                    <p className="mb-2 text-lg">Select a thread to view messages</p>
                    <p className="text-sm">Choose a conversation from the list to see the chat history</p>
                  </div>
                </div>
              )}
        </div>
      </div>
    </div>
  )
}
